import {useCallback, useEffect, useState} from "react";

import {getNextElementInTabIndex} from "./utils";

/**
 * This utility listens to focus events on the input element.
 * It remembers where the element received focus from and
 * returns utils which allow you to return focus to that element or the next.
 */
export const useFocusedFrom = (el: HTMLElement | null) => {
  const [focusedFrom, setFocusedFrom] = useState<HTMLElement | null>(null);
  const [nextElement, setNextElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!(el instanceof HTMLElement)) return;

    const rememberPreviouslyFocusedEl = (e: FocusEvent) => {
      if (e.relatedTarget instanceof HTMLElement && !focusedFrom) {
        const next = getNextElementInTabIndex(e.relatedTarget, el);
        setNextElement(next);
        setFocusedFrom(e.relatedTarget);
      }
    };

    el?.addEventListener("focusin", rememberPreviouslyFocusedEl);

    return () => {
      el?.removeEventListener("focusin", rememberPreviouslyFocusedEl);
    };
  }, [el, focusedFrom, nextElement]);

  const restoreFocus = useCallback(
    (e: FocusEvent | KeyboardEvent) => {
      e.preventDefault();
      focusedFrom?.focus();
      setFocusedFrom(null);
    },
    [focusedFrom],
  );

  const focusNext = useCallback(
    (e: FocusEvent | KeyboardEvent) => {
      e.preventDefault();
      nextElement?.focus();
      setNextElement(null);
    },
    [nextElement],
  );

  return {
    restoreFocus,
    focusNext,
  };
};
